"use client";

/**
 * Заглушка для юзера без active-тенанта: сервер ещё не поднят/не авторизован.
 * Тексты по статусу из bridge; действие одно — вернуться в бот.
 */
import { useEffect, useState } from "react";
import { bg, text, corners } from "@/lib/tokens/semantic";
import { mix } from "@/lib/tokens/mix";
import { colors } from "@/lib/tokens/primitives";
import { Icon } from "@kit/core";

const COPY: Record<string, { title: string; body: string }> = {
  none: {
    title: "Сервер ещё не поднят",
    body: "Вернись в бот и нажми «Попробовать бесплатно» — сервер поднимется за минуту.",
  },
  provisioning: {
    title: "Сервер поднимается",
    body: "Почти готово — бот напишет, как только сервер выйдет на связь.",
  },
  booting: {
    title: "Сервер поднимается",
    body: "Почти готово — бот напишет, как только сервер выйдет на связь.",
  },
  awaiting_auth: {
    title: "Остался один шаг",
    body: "Сервер готов — вернись в бот и подключи подписку.",
  },
  stopped: {
    title: "Сервер на паузе",
    body: "Триал закончился. Загляни в бот, чтобы продолжить.",
  },
};
const FALLBACK = {
  title: "Сервер недоступен",
  body: "Вернись в бот — он подскажет следующий шаг.",
};
// Self-host: сервер живёт на машине юзера, бот тут не поможет.
const LOCAL_FALLBACK = {
  title: "Локальный сервер не на связи",
  body: "Проверь машину, где запускал gem up — агент оживёт, как только сервер выйдет в сеть.",
};

export function ServerPending({ status, mode }: { status: string; mode?: string }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return null;
  const copy = COPY[status] ?? (mode === "local" ? LOCAL_FALLBACK : FALLBACK);

  const close = () => {
    const tg = window.Telegram?.WebApp;
    tg?.HapticFeedback?.impactOccurred("light");
    tg?.close();
  };

  return (
    <div style={{
      minHeight: "100dvh",
      background: `linear-gradient(180deg, ${bg.sunken} 0%, ${bg.base} 50%, ${bg.elevated} 100%)`,
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      padding: "min(32px, 8vw)",
      fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      <div style={{
        width: 80, height: 80, borderRadius: "var(--cg-radius-2xl)",
        background: `linear-gradient(135deg, ${bg.surface} 0%, ${bg.surfaceHover} 100%)`,
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: `0 8px 32px ${mix(colors.black, 30)}, inset 0 1px 0 ${bg.surfaceActive}`,
        marginBottom: 24,
        animation: "float 3s ease-in-out infinite",
      }}>
        <Icon name="solar:bolt-bold-duotone" size="xl" color="accent" />
      </div>
      <h1 style={{
        color: text.primary, fontSize: 22, fontWeight: 700,
        margin: 0, marginBottom: 8, letterSpacing: "-0.02em",
        textAlign: "center",
      }}>
        {copy.title}
      </h1>
      <p style={{
        color: text.muted, fontSize: 15, textAlign: "center",
        margin: 0, marginBottom: 32, lineHeight: 1.5, maxWidth: 280,
      }}>
        {copy.body}
      </p>
      <button onClick={close} style={{
        padding: "16px 28px", minHeight: 48,
        background: bg.surface, borderRadius: corners.card,
        border: `1px solid ${bg.surfaceHover}`,
        color: text.primary, fontSize: 15, fontWeight: 600,
        cursor: "pointer",
      }}>
        Вернуться в бот
      </button>
      <style jsx global>{`
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @media (prefers-reduced-motion: reduce) {
          * { animation: none !important; }
        }
      `}</style>
    </div>
  );
}
